import { useEffect, useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import MicIcon from '@mui/icons-material/Mic';
import { fileApi } from '../api/file_api';
import type { AssociatedFileInfo } from '../api/file_association_api';

interface VoiceRecordingPlayerProps {
  attachments: AssociatedFileInfo[];
}

/**
 * Renders an audio player for a voice-created task's recording.
 *
 * The recording is stored as a regular file association on the request
 * (content_type "audio/mpeg"), so we just find it among the request's
 * attachments and resolve an inline signed URL to play it.
 */
export function VoiceRecordingPlayer({ attachments }: VoiceRecordingPlayerProps) {
  const audio = attachments.find(a => a.content_type?.startsWith('audio/'));
  const [url, setUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!audio) {
      setUrl(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    fileApi
      .getDownloadUrl(audio.association_uuid, 'inline')
      .then(result => {
        if (!cancelled) setUrl(result.url);
      })
      .catch(err => console.error('Failed to load voice recording:', err))
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [audio?.association_uuid]);

  // No recording uploaded yet (still being downloaded from Twilio).
  if (!audio) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
        <MicIcon fontSize="small" sx={{ color: 'text.secondary' }} />
        <Typography variant="body2" color="text.secondary">
          Voice recording is being processed…
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        mt: 2,
        p: 2,
        borderRadius: 1,
        bgcolor: 'background.default',
        border: 1,
        borderColor: 'divider',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <MicIcon fontSize="small" sx={{ color: 'text.secondary' }} />
        <Typography variant="subtitle2">Voice recording</Typography>
      </Box>
      {loading || !url ? (
        <CircularProgress size={20} />
      ) : (
        <audio controls src={url} style={{ width: '100%' }}>
          Your browser does not support audio playback.
        </audio>
      )}
    </Box>
  );
}
